import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { fetchComparableCandidates, type ComparableArtist, type ComparableCandidate } from "@/api/discover";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  artistId: string;
  /** The artist's current selected comparables; drives the in-picker selection state. */
  comparables: ComparableArtist[];
  onAdd: (candidate: ComparableCandidate) => Promise<void>;
  onRemove: (comparable: ComparableArtist) => Promise<void>;
  /** Add/remove failure from the parent, shown here because the picker covers the page. */
  error?: string | null;
};

const SEARCH_DELAY_MS = 250;

/** The selected comparable a candidate row refers to, if any. */
function selectedComparableFor(candidate: ComparableCandidate, comparables: ComparableArtist[]): ComparableArtist | null {
  return (
    comparables.find((c) =>
      candidate.kind === "public" ? c.public_artist.id === candidate.id : c.resolved_artist_id === candidate.id,
    ) ?? null
  );
}

/**
 * Multi-select picker: adding or removing an artist keeps it open so several can be
 * chosen in one session. Escape, clicking outside, the X or Done close it.
 */
export default function AddComparableDialog({ open, onOpenChange, artistId, comparables, onAdd, onRemove, error: actionError }: Props) {
  const [candidates, setCandidates] = useState<ComparableCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [addingKey, setAddingKey] = useState<string | null>(null);
  // Bumped after each add/remove so `already_selected` stays current while the picker is open.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Reads stored artists from the database only; no provider calls, no collection.
    const timer = setTimeout(() => {
      fetchComparableCandidates(artistId, query)
        .then((payload) => {
          if (!cancelled) setCandidates(payload.candidates ?? []);
        })
        .catch(() => {
          if (!cancelled) setError("We couldn't load artists. Please try again.");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, query ? SEARCH_DELAY_MS : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, artistId, query, reloadKey]);

  const handleToggle = async (candidate: ComparableCandidate, selected: ComparableArtist | null) => {
    setAddingKey(`${candidate.kind}-${candidate.id}`);
    try {
      if (selected) await onRemove(selected);
      else await onAdd(candidate);
    } finally {
      setAddingKey(null);
      setReloadKey((key) => key + 1);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add comparable artists</DialogTitle>
          <DialogDescription>
            Choose artists whose touring history is relevant to where you want to play. Add as many as you like.
          </DialogDescription>
        </DialogHeader>

        <Input
          placeholder="Search artists by name"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search artists"
        />

        {actionError && (
          <p role="alert" className="text-sm text-destructive">
            {actionError}
          </p>
        )}

        <div className="max-h-80 space-y-2 overflow-y-auto" role="list" aria-label="Artists you can add">
          {loading && candidates.length === 0 && <p className="text-sm text-muted-foreground">Loading artists…</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}
          {!loading && !error && candidates.length === 0 && (
            <div className="rounded-lg border border-border bg-background/40 p-4 text-sm text-muted-foreground">
              {query.trim() ? "No matching artists." : "No artists available yet."}
            </div>
          )}
          {candidates.map((candidate) => {
            const key = `${candidate.kind}-${candidate.id}`;
            const selected = selectedComparableFor(candidate, comparables);
            const busy = addingKey === key;
            return (
              <div
                key={key}
                role="listitem"
                className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background/40 p-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground">{candidate.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {candidate.genre || "Genre unknown"}
                    {candidate.has_venue_history && (
                      <span className="text-emerald-600 dark:text-emerald-300"> · Venue history available</span>
                    )}
                  </p>
                </div>
                {selected ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void handleToggle(candidate, selected)}
                    disabled={addingKey !== null}
                    aria-pressed
                    aria-label={`Remove ${candidate.name}`}
                  >
                    {busy ? "Removing…" : "✓ Added"}
                  </Button>
                ) : candidate.already_selected ? (
                  <span className="shrink-0 text-xs font-medium text-muted-foreground" aria-label={`${candidate.name} already added`}>
                    Added
                  </span>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => void handleToggle(candidate, null)}
                    disabled={addingKey !== null}
                    aria-pressed={false}
                    aria-label={`Add ${candidate.name}`}
                  >
                    {busy ? "Adding…" : "Add"}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            Don't see the right artist?{" "}
            <Link to="/artists" className="font-medium text-primary hover:underline">
              Find more artists
            </Link>
          </p>
          <DialogClose asChild>
            <Button size="sm" variant="secondary">
              Done{comparables.length ? ` (${comparables.length} selected)` : ""}
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
